import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";

/**
 * Deep link from a comment/mention notification.
 *
 * The bell links to `<detail url>?tab=conversation&comment=<id>` (tenders use
 * `message=<id>` — same idea, their thread rows are TenderMessages). Attach the
 * returned ref to the discussion section and it is scrolled into view once the page
 * has its data, instead of leaving the reader at the top of a long detail page.
 *
 * `ready` should be the "data is on screen" condition (e.g. `!isLoading`); the scroll
 * fires once, so closing/reopening things afterwards does not yank the page back.
 */
export function useConversationDeepLink<T extends HTMLElement = HTMLDivElement>(
  ready: boolean = true,
) {
  const [searchParams] = useSearchParams();
  const commentId = searchParams.get("comment") ?? searchParams.get("message");
  const wantsConversation = searchParams.get("tab") === "conversation" || !!commentId;

  const ref = useRef<T>(null);
  const scrolled = useRef(false);

  useEffect(() => {
    if (!wantsConversation || !ready || scrolled.current || !ref.current) return;
    scrolled.current = true;
    ref.current.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [wantsConversation, ready]);

  return { ref, commentId, wantsConversation };
}
